import * as React from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { formatDate } from "@progress/kendo-intl";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import { FormNumericTextbox } from "../../components/FormNumericTextbox";
import { useQueryGetOEPeriodList } from "../../data/services/openEnrollmentService";
import {
  useMutatePostFlexContributionLimits,
  useQueryGetFlexContributionLimits,
} from "../../data/services/flexBenefitsService";

const numberOrNull = (value) =>
  value === "" || value === undefined || value === null ? null : value;

const FlexContributionLimits = () => {
  const [selectedOEPeriod, setSelectedOEPeriod] = React.useState(null);

  const { isLoading: oePeriodsLoading, data: oePeriods } =
    useQueryGetOEPeriodList();

  const oePeriodId = selectedOEPeriod?.oePeriodId;

  const { isLoading: limitsLoading, data: limitsResponse } =
    useQueryGetFlexContributionLimits(oePeriodId);

  const saveHandler = useMutatePostFlexContributionLimits();

  React.useEffect(() => {
    if (
      !oePeriodsLoading &&
      oePeriods?.data?.length > 0 &&
      !selectedOEPeriod
    ) {
      setSelectedOEPeriod(oePeriods.data[0]);
    }
  }, [oePeriodsLoading, oePeriods, selectedOEPeriod]);

  const limits = limitsResponse?.data;

  const initialValues = {
    hsaIndividual: limits?.hsaIndividual ?? "",
    hsaFamily: limits?.hsaFamily ?? "",
    fsa: limits?.fsa ?? "",
    dca: limits?.dca ?? "",
  };

  const handleSubmit = async (item) => {
    try {
      await saveHandler.mutateAsync({
        oePeriodId,
        hsaIndividual: numberOrNull(item.hsaIndividual),
        hsaFamily: numberOrNull(item.hsaFamily),
        fsa: numberOrNull(item.fsa),
        dca: numberOrNull(item.dca),
      });
    } catch {
      // Error state is surfaced via ErrorBanner through saveHandler.isError.
      // The catch prevents an unhandled promise rejection when the API fails.
    }
  };

  return (
    <div>
      <h2 style={{ marginBlockEnd: 0 }}>Flex Benefit Contribution Limits</h2>
      <p style={{ color: "rgb(120 128 135)", fontSize: "0.9em", marginTop: 4 }}>
        Maximum contribution amounts shown to portal users on the HSA, FSA and
        DCA pages. Limits apply to all groups in the selected OE period. Leave a
        field blank to hide that label.
      </p>

      <div style={{ margin: "16px 0", maxWidth: 400 }}>
        <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
          OE Period
        </label>
        <DropDownList
          style={{ width: "300px" }}
          fillMode={"outline"}
          data={oePeriods?.data ?? []}
          textField="oePeriodName"
          dataItemKey="oePeriodId"
          value={selectedOEPeriod}
          onChange={(event) => setSelectedOEPeriod(event.target.value)}
          loading={oePeriodsLoading}
        />
      </div>

      {limits?.lastUpdatedDate && limits?.lastUpdatedBy ? (
        <span style={{ color: "rgb(152 160 167)", fontSize: "0.9em" }}>
          Last updated:{" "}
          {formatDate(new Date(limits.lastUpdatedDate + "Z"), "G")} by{" "}
          {limits.lastUpdatedBy}
        </span>
      ) : null}

      <ErrorBanner handler={saveHandler} onError={() => {}} />
      <SuccessBanner handler={saveHandler} message={"Successfully saved."} />

      {oePeriodId && limitsLoading ? (
        <LoadingIndicator />
      ) : (
        <div style={{ marginTop: "20px" }}>
          <Form
            key={`${oePeriodId}-${limits?.lastUpdatedDate ?? "new"}`}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            render={() => (
              <FormElement horizontal={false} style={{ maxWidth: 600 }}>
                <Field
                  id={"hsaIndividual"}
                  name={"hsaIndividual"}
                  label={"HSA Individual - Max Contribution"}
                  component={FormNumericTextbox}
                  format={"c0"}
                  min={0}
                />
                <Field
                  id={"hsaFamily"}
                  name={"hsaFamily"}
                  label={"HSA Family - Max Contribution"}
                  component={FormNumericTextbox}
                  format={"c0"}
                  min={0}
                />
                <Field
                  id={"fsa"}
                  name={"fsa"}
                  label={"FSA - Max Contribution"}
                  component={FormNumericTextbox}
                  format={"c0"}
                  min={0}
                />
                <Field
                  id={"dca"}
                  name={"dca"}
                  label={"DCA - Max Contribution"}
                  component={FormNumericTextbox}
                  format={"c0"}
                  min={0}
                />
                <div style={{ marginTop: "30px" }}>
                  <Button
                    type="submit"
                    themeColor={"success"}
                    disabled={!oePeriodId}
                  >
                    {"Save"}
                  </Button>
                </div>
              </FormElement>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default FlexContributionLimits;
